import { WORLD_HEIGHT, WORLD_WIDTH } from "../game/constants";
import type { Vec2 } from "../game/types";

export class InputSystem {
  aiming = false;
  pointer: Vec2 = { x: WORLD_WIDTH * 0.7, y: WORLD_HEIGHT * 0.4 };
  private down = false;
  private canvas: HTMLCanvasElement | null = null;
  private releaseHandler: ((point: Vec2) => void) | null = null;

  attach(canvas: HTMLCanvasElement, onRelease: (point: Vec2) => void): void {
    this.detach();
    this.canvas = canvas;
    this.releaseHandler = onRelease;
    canvas.addEventListener("pointerdown", this.onDown);
    canvas.addEventListener("pointermove", this.onMove);
    canvas.addEventListener("pointerup", this.onUp);
    canvas.addEventListener("pointercancel", this.onUp);
  }

  detach(): void {
    this.canvas?.removeEventListener("pointerdown", this.onDown);
    this.canvas?.removeEventListener("pointermove", this.onMove);
    this.canvas?.removeEventListener("pointerup", this.onUp);
    this.canvas?.removeEventListener("pointercancel", this.onUp);
    this.canvas = null;
    this.releaseHandler = null;
    this.aiming = false;
    this.down = false;
  }

  private toWorld(event: PointerEvent): Vec2 {
    const canvas = this.canvas;
    if (!canvas) return this.pointer;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * WORLD_WIDTH,
      y: ((event.clientY - rect.top) / rect.height) * WORLD_HEIGHT,
    };
  }

  private onDown = (event: PointerEvent): void => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    try {
      this.canvas?.setPointerCapture(event.pointerId);
    } catch {
      /* synthetic or already-released pointers */
    }
    this.pointer = this.toWorld(event);
    this.down = true;
    this.aiming = true;
  };

  private onMove = (event: PointerEvent): void => {
    if (!this.down && event.pointerType === "mouse" && event.buttons === 0) {
      this.pointer = this.toWorld(event);
      return;
    }
    if (!this.down) return;
    event.preventDefault();
    this.pointer = this.toWorld(event);
  };

  private onUp = (event: PointerEvent): void => {
    if (!this.down) return;
    event.preventDefault();
    this.pointer = this.toWorld(event);
    this.down = false;
    this.aiming = false;
    this.releaseHandler?.(this.pointer);
  };
}
